import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { BookOpen, Plus, Pencil, Trash2, Sparkles, TrendingUp, Check, X, HelpCircle, Wand2, Loader2 } from 'lucide-react';

const API = `${window.location.origin}/api`;

export default function KnowledgeBase() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // article obj or {} for new
  const [promoting, setPromoting] = useState(null); // gap obj
  const [confirm, setConfirm] = useState(null); // { type: 'article'|'gap', item }
  const [generating, setGenerating] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/kb`, { headers });
      setArticles(res.data.articles || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchGaps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/gaps`, { headers, params: { status: 'open' } });
      setGaps(res.data.gaps || []);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchArticles(); fetchGaps(); }, [fetchArticles, fetchGaps]);

  const restoreArticle = async (article) => {
    try {
      await axios.post(`${API}/ai/kb`, { question: article.question, answer: article.answer, tags: article.tags || [] }, { headers });
      toast.success('Article restored');
      fetchArticles();
    } catch (e) { toast.error('Failed to restore article'); }
  };

  const reopenGap = async (id) => {
    try {
      await axios.post(`${API}/ai/gaps/${id}/reopen`, {}, { headers });
      toast.success('Gap restored');
      fetchGaps();
    } catch (e) { toast.error('Failed to restore gap'); }
  };

  const doConfirm = async () => {
    if (!confirm) return;
    const { type, item } = confirm;
    setConfirm(null);
    if (type === 'article') {
      try {
        await axios.delete(`${API}/ai/kb/${item.id}`, { headers });
        setArticles((prev) => prev.filter((a) => a.id !== item.id));
        toast('Article deleted', {
          description: (item.question || '').slice(0, 70),
          duration: 8000,
          action: { label: 'Undo', onClick: () => restoreArticle(item) },
        });
      } catch (e) { toast.error('Failed to delete'); }
    } else {
      try {
        await axios.post(`${API}/ai/gaps/${item.id}/dismiss`, {}, { headers });
        setGaps((prev) => prev.filter((g) => g.id !== item.id));
        toast('Gap dismissed', {
          description: (item.sample_question || '').slice(0, 70),
          duration: 8000,
          action: { label: 'Undo', onClick: () => reopenGap(item.id) },
        });
      } catch (e) { toast.error('Failed to dismiss'); }
    }
  };

  const deleteArticle = (article) => setConfirm({ type: 'article', item: article });
  const dismissGap = (gap) => setConfirm({ type: 'gap', item: gap });

  const generateFromPlatform = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/kb/generate`, {}, { headers });
      const d = res.data || {};
      const total = (d.added || 0) + (d.updated || 0) + (d.ai_added || 0) + (d.ai_updated || 0);
      if (d.ai_available === false) {
        toast.success(`Added ${d.added || 0} baseline articles. AI expansion unavailable right now.`);
      } else {
        toast.success(`Knowledge base refreshed — ${total} article(s) added or updated.`);
      }
      fetchArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to auto-fill knowledge base');
    } finally { setGenerating(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 md:ml-64 pt-16 md:pt-6 p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-amber-500" /> Juan Knowledge Base
            </h1>
            <p className="text-sm text-slate-500">Train Juan to answer HandyBayani questions, and turn common problems into help articles.</p>
          </div>
          {tab === 'articles' && (
            <div className="flex items-center gap-2">
              <Button onClick={generateFromPlatform} disabled={generating} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="kb-generate-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                {generating ? 'Auto-filling...' : 'Auto-fill from Platform'}
              </Button>
              <Button onClick={() => setEditing({})} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="kb-new-article-btn">
                <Plus className="w-4 h-4 mr-2" /> New Article
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-5">
          {[['articles', 'Articles', BookOpen, articles.length], ['gaps', 'Knowledge Gaps', HelpCircle, gaps.length]].map(([key, label, Icon, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${tab === key ? 'bg-slate-900 text-white' : 'bg-white border border-stone-200 text-slate-600 hover:bg-stone-50'}`}
              data-testid={`kb-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
              <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs flex items-center justify-center ${tab === key ? 'bg-white/20' : 'bg-stone-100'}`}>{count}</span>
            </button>
          ))}
        </div>

        {tab === 'articles' ? (
          loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>
          ) : articles.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <BookOpen className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No knowledge base articles yet</p>
              <p className="text-slate-400 text-sm mt-1">Add articles so Juan can answer customer and provider questions accurately.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="bg-white rounded-xl p-4 shadow-sm flex items-start gap-3" data-testid={`kb-article-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{a.question}</p>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-3 whitespace-pre-wrap">{a.answer}</p>
                    {(a.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {a.tags.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">{t}</span>)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setEditing(a)} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`kb-edit-${a.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteArticle(a)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`kb-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )
        ) : (
          gaps.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <HelpCircle className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No knowledge gaps</p>
              <p className="text-slate-400 text-sm mt-1">When Juan can't answer a question, it shows up here — grouped by how often it's asked.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {gaps.map((g) => (
                <div key={g.id} className="bg-white rounded-xl p-4 shadow-sm flex items-start gap-3" data-testid={`kb-gap-${g.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`kb-gap-count-${g.id}`}>
                        <TrendingUp className="w-3 h-3" /> asked {g.count} time{g.count === 1 ? '' : 's'}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-slate-500 font-medium capitalize">{g.role}</span>
                    </div>
                    <p className="text-sm text-slate-800 mt-2">{g.sample_question}</p>
                  </div>
                  <button onClick={() => setPromoting(g)} className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600" data-testid={`kb-promote-${g.id}`}>
                    <Check className="w-4 h-4" /> Add Answer
                  </button>
                  <button onClick={() => dismissGap(g)} className="p-1.5 text-slate-400 hover:text-red-500" title="Dismiss" data-testid={`kb-dismiss-${g.id}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )
        )}
      </main>

      {editing && (
        <ArticleEditor article={editing} headers={headers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchArticles(); }} />
      )}
      {promoting && (
        <PromoteGap gap={promoting} headers={headers} onClose={() => setPromoting(null)} onSaved={() => { setPromoting(null); fetchGaps(); fetchArticles(); }} />
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="kb-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === 'gap' ? 'Dismiss this knowledge gap?' : 'Delete this article?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === 'gap'
                ? 'This removes the gap from the open list. You can undo this right after.'
                : 'Juan will no longer use this article to answer questions. You can undo this right after.'}
              <span className="block mt-2 font-medium text-slate-700">
                {confirm?.type === 'gap' ? confirm?.item?.sample_question : confirm?.item?.question}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kb-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="kb-delete-confirm-btn"
            >
              {confirm?.type === 'gap' ? 'Dismiss' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ArticleEditor({ article, headers, onClose, onSaved }) {
  const [question, setQuestion] = useState(article.question || '');
  const [answer, setAnswer] = useState(article.answer || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) { toast.error('Question and answer are required'); return; }
    setSaving(true);
    try {
      if (article.id) await axios.put(`${API}/ai/kb/${article.id}`, { question, answer }, { headers });
      else await axios.post(`${API}/ai/kb`, { question, answer }, { headers });
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{article.id ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium text-slate-700">Question</label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. How do I cancel a booking?" className="mt-1.5 h-11" data-testid="kb-question-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Answer</label>
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="The answer Juan will use..." className="mt-1.5 min-h-[160px]" data-testid="kb-answer-input" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="kb-save-article-btn">
            {saving ? 'Saving...' : 'Save Article'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PromoteGap({ gap, headers, onClose, onSaved }) {
  const [question, setQuestion] = useState(gap.sample_question || '');
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!answer.trim()) { toast.error('Please write an answer'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/ai/gaps/${gap.id}/promote`, { question, answer }, { headers });
      toast.success('Added to knowledge base');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Answer this question</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="text-xs text-slate-500 bg-stone-50 rounded-lg px-3 py-2">Asked {gap.count} time{gap.count === 1 ? '' : 's'} by {gap.role}s</div>
          <div>
            <label className="text-sm font-medium text-slate-700">Question</label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} className="mt-1.5 h-11" data-testid="promote-question-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Answer</label>
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Write the official answer Juan should give..." className="mt-1.5 min-h-[160px]" data-testid="promote-answer-input" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="promote-save-btn">
            {saving ? 'Saving...' : 'Save to Knowledge Base'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
